import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import { useEffect, useMemo, useState } from 'react';

import * as Client from 'api/client';
import { useAuth } from 'providers/Auth';
import Context from './Context';

import { ANNOTATION_TYPES, STATUS, shop as _shop } from 'config/const';
import { useLoading } from 'providers/Loading';

let historySlug = '';
const getAnnotationFns = (annotations) => {
  const findAnnotation = (key = '') =>
    annotations.find((annotation) => annotation.key === key) || {};
  const findCustomAnnotations = () =>
    annotations.filter((annotation) => !annotation.isSystem) || [];
  const findDynamicItems = () =>
    annotations.filter(
      (annotation) =>
        !annotation.isSystem && annotation.type !== ANNOTATION_TYPES.ITEM_LIST,
    ) || [];
  const findItemList = () =>
    annotations.filter(
      (annotation) =>
        !annotation.isSystem && annotation.type === ANNOTATION_TYPES.ITEM_LIST,
    ) || [];
  const findAnnotations = (keys = []) => keys.map((key) => findAnnotation(key));
  const findPrerequisities = (key, value, isSystem = false) => {
    if (value === undefined || value === null) return [];
    return (
      annotations.filter((annotation) => {
        if (
          !annotation.prerequisities ||
          Boolean(annotation.isSystem) !== isSystem
        )
          return false;
        if (Array.isArray(annotation.prerequisities))
          return annotation.prerequisities.find((pre) => {
            const [[k, v]] = Object.entries(pre);
            return k === key && v === value;
          });
        return annotation.prerequisities[key] === value;
      }) || []
    );
  };

  return {
    findAnnotation,
    findDynamicItems,
    findItemList,
    findAnnotations,
    findPrerequisities,
    findCustomAnnotations,
  };
};

const Provider = ({ children, mock }) => {
  const [shop, setShop] = useState(isEmpty(_shop) ? mock : _shop);
  const [pageInfo, setPageInfo] = useState({});
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [contentCategories, setContentCategories] = useState([]);
  const [contentPages, setContentPages] = useState([]);
  const [settings, setSettings] = useState([]);
  const [categoryTags, setCategoryTags] = useState([]);
  const [categoryInfos, setCategoryInfos] = useState([]);
  const [fetched, setFetched] = useState(false);

  const { auth } = useAuth();
  const { open, close } = useLoading();

  const getProduct = (productId) => {
    return products.find(({ id }) => id === Number(productId));
  };

  const getProducts = () =>
    Client.getProducts(auth.idToken).then((res) => {
      if (!res.err)
        setProducts(
          res
            .filter(({ status }) => status !== STATUS.OFF)
            .sort((a, b) => a.order - b.order),
        );

      return !res.err;
    });

  const getCategories = () =>
    Client.getCategories(auth.idToken).then((res) => {
      if (!res.err)
        setCategories(
          res
            .filter(({ status }) => status === STATUS.ON)
            .sort((a, b) => a.order - b.order),
        );

      return !res.err;
    });

  const getCategoryTags = () =>
    Client.getCategoryTags(auth.idToken).then((res) => {
      if (!res.err) setCategoryTags(res);
      return !res.err;
    });

  const getContentCategories = (options = {}) =>
    Client.getContentCategory(auth.idToken, options).then((res) => {
      if (!res.err) setContentCategories(res);
      return !res.err;
    });

  const getContentPages = (options = {}) =>
    Client.getContentPages(auth.idToken, options).then((res) => {
      if (!res.err) setContentPages(res);
      return !res.err;
    });

  const getContentPageById = (id, options = {}) =>
    Client.getContentPageById(auth.idToken, id, options).then((res) => {
      return !res.err ? res : false;
    });

  const getShop = () =>
    Client.getShop(auth.accessToken, auth.userId).then((res) => {
      setSettings(get(res, 'settings.formica'));
      setCategoryInfos(get(res, 'settings.formica.categoryInfos'));
      setShop(res);
      return !res.err ? res : false;
    });

  const updatePageInfo = (key, value) => {
    setPageInfo({ ...pageInfo, [key]: value });
  };

  const setHistorySlug = (slug) => {
    historySlug = slug;
  };
  const getPageInfo = (key) => pageInfo[key];

  // const getSettings = () => {
  // return Client.getSettings(auth.idToken).then((res) => {
  //   if (!res.err) setSettings(res);
  //   return !res.err ? res : false;
  // });
  // };

  async function init() {
    open();
    const resps = await Promise.all([
      // getProducts(),
      // getCategories(),
      // getContentCategories(),
      getShop(),
    ]);
    const fetched = resps.reduce((isSuccess, resp) => isSuccess && resp, true);
    close();
    setFetched(fetched);
  }

  useEffect(() => {
    if (!auth.idToken || !auth.idToken.token || fetched) return;
    init();
  }, [auth]);

  useEffect(() => {
    if (!fetched || isEmpty(settings) || isEmpty(categoryTags)) return;
    const categoryTagsWithSettingInfo = categoryTags.map((v) => {
      const categoryTagInfo = settings.find(
        ({ category_tag_id }) => category_tag_id === v.id,
      );
      return { ...v, ...categoryTagInfo };
    });
    setCategoryTags(categoryTagsWithSettingInfo);
  }, [fetched]);

  const annotations = get(shop, 'annotations', []);
  const {
    findAnnotation,
    findDynamicItems,
    findItemList,
    findAnnotations,
    findPrerequisities,
    findCustomAnnotations,
  } = useMemo(() => getAnnotationFns(annotations), [annotations]);

  const state = {
    shop: shop || {},
    products,
    categories,
    contentCategories,
    contentPages,
    fetched,
    settings,
    categoryInfos,
    historySlug,
    setHistorySlug,
  };

  const api = {
    getProducts,
    getCategories,
    getCategoryTags,
    getContentCategories,
    getContentPages,
    getContentPageById,
    // getSettings,
  };
  const getInfo = {
    getProduct,
    categoryTags,
    deliveryFee: get(shop, 'settings.delivery_fee', {}),
    branches: get(shop, 'branches', []),
    annotations,
    hasShop: isEmpty(shop),
    products,
    categories,
    contentCategories,
    contentPages,
    updatePageInfo,
    getPageInfo,
  };

  const method = {
    findAnnotation,
    findAnnotations,
    findPrerequisities,
    findDynamicItems,
    findItemList,
    findCustomAnnotations,
  };

  const props = { ...state, ...api, ...getInfo, ...method };

  return <Context.Provider value={{ ...props }}>{children}</Context.Provider>;
};

export default Provider;
